/**
 * Canonical Dial schema. Single source of truth.
 * Mirrored in:
 *  - scripts/seed_sample.ts        (sample data generator)
 *  - mcp/server.ts                  (MCP resource shape)
 *  - future: ios/Dial/Models.swift  (App Intent AppEntity)
 */

export const SCHEMA_VERSION = 1 as const;

export const RELATIONSHIP_KINDS = [
  'family',
  'partner',
  'best_friend',
  'close_friend',
  'school',
  'college',
  'workplace',
  'neighbor',
  'mentor',
  'acquaintance',
  'occasional',
] as const;
export type RelationshipKind = (typeof RELATIONSHIP_KINDS)[number];

export const LIFE_AREAS = ['home', 'work', 'social', 'fitness', 'spiritual', 'creative', 'investment'] as const;
export type LifeArea = (typeof LIFE_AREAS)[number];

export const CONTACT_CHANNELS = ['call', 'whatsapp', 'imessage', 'meet', 'mail', 'instagram'] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];

export const EVENT_KINDS = ['birthday', 'anniversary', 'promotion', 'wedding', 'other'] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export const TIERS = [
  'loner',
  'small_circle',
  'active_network',
  'wide_network',
  'connector',
] as const;
export type Tier = (typeof TIERS)[number];

export const TIER_THRESHOLDS_ACTIVE_COUNT: Record<Tier, number> = {
  loner: 0,
  small_circle: 10,
  active_network: 25,
  wide_network: 60,
  connector: 120,
};

export const TIER_DISPLAY: Record<Tier, { name: string; tagline: string }> = {
  loner: { name: 'Loner', tagline: 'akelaa hi sab kuch' },
  small_circle: { name: 'Small Circle', tagline: 'chaar yaar' },
  active_network: { name: 'Active Network', tagline: 'har shaam ki mehfil' },
  wide_network: { name: 'Wide Network', tagline: 'har city mein dost' },
  connector: { name: 'Connector', tagline: 'sab ke beech ki jodi' },
};

export const RELATIONSHIP_DISPLAY: Record<RelationshipKind, string> = {
  family: 'Family',
  partner: 'Partner',
  best_friend: 'Best friend',
  close_friend: 'Close friend',
  school: 'School',
  college: 'College',
  workplace: 'Workplace',
  neighbor: 'Neighbour',
  mentor: 'Mentor',
  acquaintance: 'Acquaintance',
  occasional: 'Occasional',
};

export interface UpcomingEvent {
  kind: EventKind;
  date: string; // YYYY-MM-DD
  note: string | null;
}

export interface Person {
  id: string;
  name: string;
  relationship_kind: RelationshipKind;
  life_areas: LifeArea[];
  city: string | null;
  last_contact_iso: string | null;
  last_contact_channel: ContactChannel | null;
  contact_frequency_per_month: number;
  warmth_score: number; // 0-100
  upcoming_event: UpcomingEvent | null;
  notes: string | null;
  occasions_shared: string[];
}

export interface Insights {
  most_neglected: Person | null;
  rising_star: Person | null;
  birthday_this_month: Person[];
  most_frequent_channel: ContactChannel;
  cities_covered: string[];
  avg_contact_frequency: number;
  weekly_meet_count: number;
}

export interface Summary {
  schema_version: typeof SCHEMA_VERSION;
  generated_at: string;
  total_contacts_known: number;
  inner_circle: Person[];
  tier: Tier;
  warmth_total: number;
  active_connection_count: number;
  insights: Insights;
  by_kind: Partial<Record<RelationshipKind, number>>;
  by_city: Record<string, number>;
}

export function tierFor(activeCount: number): Tier {
  if (activeCount >= TIER_THRESHOLDS_ACTIVE_COUNT.connector) return 'connector';
  if (activeCount >= TIER_THRESHOLDS_ACTIVE_COUNT.wide_network) return 'wide_network';
  if (activeCount >= TIER_THRESHOLDS_ACTIVE_COUNT.active_network) return 'active_network';
  if (activeCount >= TIER_THRESHOLDS_ACTIVE_COUNT.small_circle) return 'small_circle';
  return 'loner';
}

export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

export function relativeContact(iso: string | null): string {
  const d = daysSince(iso);
  if (d == null) return 'no record';
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  if (d < 30) return `${Math.floor(d / 7)} weeks ago`;
  if (d < 365) return `${Math.floor(d / 30)} months ago`;
  return `${Math.floor(d / 365)} years ago`;
}

export const formatCount = (n: number): string => {
  const r = Math.round(n);
  if (r < 1000) return r.toString();
  if (r < 100_000) return r.toLocaleString('en-IN');
  return `${(r / 1000).toFixed(1)}K`;
};
